import { v4 as uuid } from "uuid";
import Axios, { CanceledError } from "axios";
import type { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";

import { getAppInsights } from "./insights";

import { config } from "@/config";
import { trim, trimEnd } from "@/services/UtilitiesService";

import type { ApiError } from "@/services/Api";
import type { Multipart } from "@/models/Multipart";

function isResponseError(error: unknown): error is ResponseError {
  return error != null && typeof error === "object" && "response" in error;
}

const makeUrl = (fragments: string | string[]): string => {
  if (typeof fragments === "string") {
    return fragments;
  }

  for (let index = 0; index < fragments.length; index++) {
    fragments[index] = trim(String(fragments[index]), "/").split("/").map(encodeURIComponent).join("/");
  }

  return trimEnd(fragments.join("/"), "/");
};

export async function actionAxiosRequest(
  baseURL: string,
  parameters: {
    verb: string;
    url: string | string[];
    config?: object | AxiosRequestConfig;
    payload?: unknown;
    multipart?: boolean;
    feature: boolean;
  },
) {
  const axios = Axios.create({
    baseURL,
    withCredentials: true,
  });

  if (!!config.instrumentationKey) {
    axios.interceptors.request.use(
      (request: any) => {
        request.metadata = { startTime: Date.now() };
        return request;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    axios.interceptors.response.use(
      (response) => {
        const appInsights = getAppInsights()!;
        const metadata = (response.config as any).metadata || {};
        const startTime = metadata.startTime;
        const duration = Date.now() - startTime;

        appInsights.trackDependencyData({
          name: `${response.config.method!.toUpperCase()} ${response.config.url}`,
          target: response.config.baseURL,
          duration: duration,
          success: true,
          responseCode: response.status,
          id: uuid(), // Generate a unique ID
        });
        return response;
      },
      (error) => {
        const appInsights = getAppInsights()!;
        const startTime = error.config.metadata.startTime;
        const duration = Date.now() - startTime;
        const response = error.response;

        let shouldLog = true;

        // Handle these two errors specifically to avoid noise
        if (response && (response.status === 404 || response.status === 503)) {
          if (response.status === 503 && response.config.url.startsWith(config.contentUrl)) {
            shouldLog = false;
          }
          if (response.status === 404 && !!response.headers["x-accepted-unity-response"]) {
            shouldLog = false;
          }
        }

        if (shouldLog) {
          appInsights.trackDependencyData({
            name: `${error.config.method.toUpperCase()} ${error.config.url}`,
            target: error.config.baseURL,
            duration: duration,
            success: false,
            responseCode: response ? response.status : "Failed",
            id: uuid(),
          });
        }

        return Promise.reject(error);
      },
    );
  }

  let axiosPromise: null | AxiosPromise = null;
  try {
    // Start the Api Request
    switch (parameters.verb) {
      case "post":
        if (parameters.multipart && parameters.payload) {
          const formData = new FormData();
          const parts = parameters.payload as Multipart[];
          parts.forEach((p) => {
            if (p.fileName) {
              formData.append(p.name, p.value as Blob, p.fileName);
            } else {
              formData.append(p.name, p.value);
            }
          });
          parameters.payload = formData;
        }
        axiosPromise = axios.post(makeUrl(parameters.url), parameters.payload, parameters.config);
        break;
      case "put":
        axiosPromise = axios.put(makeUrl(parameters.url), parameters.payload, parameters.config);
        break;
      case "patch":
        axiosPromise = axios.patch(makeUrl(parameters.url), parameters.payload, parameters.config);
        break;
      case "delete":
        axiosPromise = axios.delete(makeUrl(parameters.url), parameters.config);
        break;
      default:
        axiosPromise = axios.get(makeUrl(parameters.url), parameters.config);
        break;
    }
  } catch (error) {
    const apiError: ApiError = {
      status: 550, // Use as a developer error
      statusText: "Developer error",
      data: error,
    };

    if (import.meta.env.MODE !== "test") console.error(error);

    throw apiError;
  }

  try {
    const result: AxiosResponse = await axiosPromise;
    if (result.status === 207 || result.status === 204 || (result.status === 403 && parameters.feature)) {
      throw { response: result } satisfies ResponseError;
    }

    // Grab the location header
    if (result.status === 201) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return result.headers.location;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return result.data;
  } catch (error) {
    const apiError: ApiError = {
      status: 550, // Use as a developer error
      statusText: "Developer error",
    };

    if (error instanceof CanceledError) {
      apiError.statusText = "Canceled";
      apiError.data = error;
    } else if (isResponseError(error)) {
      apiError.status = error.response.status === 404 ? 204 : error.response.status;
      apiError.statusText = error.response.statusText;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      apiError.data = error.response.data;
    } else {
      apiError.data = error;
      if (import.meta.env.MODE !== "test") console.error(error);
    }

    throw apiError;
  }
}

interface ResponseError {
  response: AxiosResponse;
}
